import { useState } from "react";
import { api } from "../api";
import { ConfirmDialog, Textarea, Field } from "./Shared";

/**
 * Finishing or cancelling a project.
 *
 * One component for both because they differ in exactly two ways — the reason
 * recorded, and whether the note is required — and building them separately
 * would let the wording drift apart.
 *
 * The required note is the whole point of the cancel path. "This project was
 * cancelled", with nothing beside it, is the kind of record that has everyone
 * reconstructing the story out of e-mail a year later; the person who knows why
 * is the one pressing the button, at the one moment they still remember.
 */
export default function CloseProjectDialog({ projectId, mode, onDone, onClose }) {
  const cancelling = mode === "cancel";
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const missingReason = cancelling && !note.trim();

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const updated = await api.post(`/api/projects/${projectId}/${mode}`, { note });
      onDone(updated);
    } catch (e) {
      setError(e.message);
      setBusy(false);
    }
  };

  return (
    <ConfirmDialog
      title={cancelling ? "Cancel this project?" : "Finish this project?"}
      confirmLabel={cancelling ? "Cancel project" : "Finish project"}
      busyLabel={cancelling ? "Cancelling…" : "Finishing…"}
      tone={cancelling ? "danger" : "primary"}
      busy={busy}
      // ConfirmDialog locks its button whenever blockers is non-empty, which is
      // how the required note is enforced on screen. The server refuses an empty
      // one as well — this only saves a round trip.
      blockers={missingReason ? ["A reason is required."] : []}
      onConfirm={submit}
      onClose={onClose}
    >
      <p style={{ margin: "0 0 16px", fontSize: 14, color: "#374151" }}>
        {cancelling
          ? "The project is archived and stops accepting data. Nothing in storage is deleted."
          : "The project is archived and stops accepting data. Its files and records stay exactly where they are."}
      </p>
      <Field
        label={cancelling ? "Why is it being cancelled?" : "Anything worth recording? (optional)"}
        hint={cancelling
          ? "Kept with the project and in its log, so the reason is still there when somebody asks later — including if an admin reopens it."
          : "Kept with the project and in its log."}
      >
        <Textarea
          value={note}
          onChange={e => setNote(e.target.value)}
          rows={3}
          placeholder={cancelling ? "Sample failed QC; customer withdrew." : ""}
        />
      </Field>
      {error && <div role="alert" style={{ marginTop: 12, color: "#dc2626", fontSize: 14 }}>{error}</div>}
    </ConfirmDialog>
  );
}
